import type { ReactNode } from 'react';
import { cn } from './cn.js';

/**
 * O painel: um título, uma frase, um destino — e o conteúdo abaixo, sem caixa.
 *
 * ## Por que ele não é um `Card`
 *
 * Até 17/09/2026 os cinco painéis do início eram `Card`, e os números deles
 * eram `<dt>`/`<dd>` nus lá dentro. Quando o KPI virou cartão — a anatomia das
 * referências de desenho: pastilha do ícone, rótulo, valor —, manter o painel
 * como cartão poria cartão dentro de cartão, que o `DESIGN.md` proíbe e que
 * desenha duas molduras para a mesma coisa.
 *
 * A casca desceu um nível: **quem é bloco destacável agora é o número**, e o
 * painel é o que sempre foi de verdade — um cabeçalho e uma grade. É a mesma
 * escolha que `StageColumn` já fazia no quadro da retenção, com o mesmo
 * argumento escrito no docblock de lá.
 *
 * ## O cabeçalho é o do `Card`, letra por letra
 *
 * Mesmo tamanho, mesmo peso, mesmo respiro — `text-[0.8125rem] font-bold` no
 * título e `text-xs text-texto-suave` na descrição. Não porque seja bonito, mas
 * porque as duas peças aparecem na MESMA tela, uma embaixo da outra: dois pesos
 * de título para a mesma função é o defeito que a decisão 3 proíbe, e foi assim
 * que o portal e a interna acabaram com dois `Card` diferentes.
 *
 * ## `footer` é o que qualifica os números, e nunca um número a mais
 *
 * A quebra por etapa da cobrança, a data da coleta, a fila sem consultor. Vem
 * abaixo de uma linha porque é leitura de segundo plano — quem olha o início de
 * manhã lê os cartões e segue. Um número que mereça o mesmo peso dos outros é
 * um `StatCard`, não uma linha de rodapé.
 */
export function Panel({
  title,
  descricao,
  actions,
  children,
  footer,
  level = 2,
  className,
}: {
  title: ReactNode;
  descricao?: ReactNode;
  /** O destino do painel — "Ver todas", "Ver a mesa". */
  actions?: ReactNode;
  children?: ReactNode;
  /** O que qualifica os números, abaixo de uma linha. Ver o cabeçalho. */
  footer?: ReactNode;
  /** O nível do título. A ordem dos cabeçalhos da página é de quem monta a página. */
  level?: 2 | 3;
  className?: string;
}) {
  const Title = level === 2 ? 'h2' : 'h3';

  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Title className="text-[0.8125rem] font-bold text-texto">{title}</Title>
          {descricao && <p className="mt-0.5 text-xs text-texto-suave">{descricao}</p>}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </header>

      {children}

      {footer !== undefined && (
        /*
         * `borda` e não `borda-suave`: aqui a linha separa na PÁGINA, e não
         * dentro de uma caixa — é a mesma linha que o cabeçalho de uma coluna
         * do quadro desenha.
         */
        <div className="border-t border-borda pt-3">{footer}</div>
      )}
    </section>
  );
}
